import {AppActions, RootState} from 'app/rootReducer';
import Toast from 'components/Basic/Toast';
import {BROWSER_ENUM} from 'interfaces';
import {Epic} from 'redux-observable';
import {concat, from, of} from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  ignoreElements,
  tap,
} from 'rxjs/operators';
import {TikTokService} from 'services/api/TikTok';
import {BROWSER, openUrl} from 'utils';

import {
  getTikTokAuthUrl,
  getTikTokAuthUrlFailure,
  getTikTokAuthUrlSuccess,
  syncTikTokAccount,
  syncTikTokAccountFailure,
  syncTikTokAccountSuccess,
  uploadToTikTok,
  uploadToTikTokFailure,
  uploadToTikTokSuccess,
} from './tiktokActions';

export const getTikTokAuthUrlEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(getTikTokAuthUrl.match),
    concatMap(({payload}) =>
      from(TikTokService.getTikTokAuthUrl(payload.audioId)).pipe(
        concatMap(({message: {authUrl}}) => {
          const isSafariBrowser = BROWSER === BROWSER_ENUM.SAFARI;

          if (isSafariBrowser) {
            window.location.href = authUrl;
          } else {
            openUrl(authUrl);
          }

          if (payload?.onSuccess) {
            payload.onSuccess();
          }

          return of(getTikTokAuthUrlSuccess({authUrl}));
        }),
        catchError((message: string) =>
          concat(of(getTikTokAuthUrlFailure(message))),
        ),
      ),
    ),
  );

export const getTikTokAuthUrlFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(getTikTokAuthUrlFailure.match),
    tap(({payload: message}) => {
      Toast({type: 'error', message});
    }),
    ignoreElements(),
  );

export const syncTikTokAccountEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(syncTikTokAccount.match),
    concatMap(({payload}) =>
      from(TikTokService.syncTikTokAccount(payload.code)).pipe(
        concatMap(({message}) => {
          if (payload?.onSuccess) {
            payload.onSuccess();
          }

          return of(syncTikTokAccountSuccess(message));
        }),
        catchError((message: string) => {
          Toast({
            type: 'error',
            message:
              typeof message === 'string'
                ? message
                : 'Failed to connect TikTok account',
          });
          return concat(of(syncTikTokAccountFailure(message)));
        }),
      ),
    ),
  );

export const syncTikTokAccountFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(syncTikTokAccountFailure.match),
    tap(({payload: message}) => {
      Toast({type: 'error', message});
    }),
    ignoreElements(),
  );

export const uploadToTikTokEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(uploadToTikTok.match),
    concatMap(({payload}) =>
      from(TikTokService.uploadToTikTok(payload.audioId)).pipe(
        concatMap(response => {
          Toast({
            type: 'success',
            message: 'Successfully uploaded to TikTok!',
          });

          if (payload?.onSuccess) {
            payload.onSuccess();
          }

          return of(uploadToTikTokSuccess(response));
        }),
        catchError((error: string) => {
          const errorMessage =
            typeof error === 'string' ? error : 'Failed to upload to TikTok';

          Toast({
            type: 'error',
            message: errorMessage,
          });

          if (payload?.onError) {
            payload.onError();
          }

          return concat(of(uploadToTikTokFailure(error)));
        }),
      ),
    ),
  );

export const TikTokEpics = [
  getTikTokAuthUrlEpic,
  getTikTokAuthUrlFailureEpic,
  syncTikTokAccountEpic,
  syncTikTokAccountFailureEpic,
  uploadToTikTokEpic,
].flatMap(epic => epic);
